import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import api from '../../services/api';
import AdminStoreSearchCombobox from '../Admin/AdminStoreSearchCombobox';

const WH_SHOPS_PAGE_SIZE = 10;

/**
 * Суперадмин: включение/отключение магазина в WH (PUT /wildberries/shops/{shop_id}).
 */
export default function WhShopTogglePanel() {
  const [search, setSearch] = useState('');
  const [userFilter, setUserFilter] = useState(null);
  const [disabledOnly, setDisabledOnly] = useState(false);
  const [stores, setStores] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [togglingPk, setTogglingPk] = useState(null);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / WH_SHOPS_PAGE_SIZE)),
    [total],
  );

  const loadStores = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.getBillingAdminWhShops({
        q: search.trim() || undefined,
        disabled_only: disabledOnly || undefined,
        page,
        page_size: WH_SHOPS_PAGE_SIZE,
      });
      setStores(Array.isArray(res?.stores) ? res.stores : []);
      setTotal(Number(res?.total) || 0);
      if (res?.page) setPage(res.page);
    } catch (e) {
      setError(e?.data?.error || e?.message || 'Ошибка загрузки');
      setStores([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [search, disabledOnly, page]);

  useEffect(() => {
    const t = setTimeout(loadStores, 300);
    return () => clearTimeout(t);
  }, [loadStores]);

  useEffect(() => {
    setPage(1);
  }, [search, disabledOnly]);

  const handleToggle = async (row, nextStatus) => {
    const label = nextStatus === 'inactive' ? 'отключить' : 'включить';
    const ok = window.confirm(
      `${label.charAt(0).toUpperCase() + label.slice(1)} магазин «${row.store_name || row.store_external_id}» `
      + `(${row.user_email}) в WH?`,
    );
    if (!ok) return;

    setTogglingPk(row.store_pk);
    setError('');
    try {
      await api.setBillingAdminWhShopStatus(row.store_pk, nextStatus);
      await loadStores();
    } catch (e) {
      setError(e?.data?.error || e?.message || 'Ошибка WH API');
    } finally {
      setTogglingPk(null);
    }
  };

  const onUserPicked = (storeRow) => {
    if (!storeRow) {
      setUserFilter(null);
      return;
    }
    const email = storeRow.user_email || '';
    setUserFilter(email ? { email } : null);
    if (email) setSearch(email);
  };

  const rangeFrom = total === 0 ? 0 : (page - 1) * WH_SHOPS_PAGE_SIZE + 1;
  const rangeTo = Math.min(page * WH_SHOPS_PAGE_SIZE, total);

  return (
    <Card className="w-100 mb-4 border-danger border-2">
      <Card.Header className="bg-danger text-white d-flex flex-wrap align-items-center gap-2">
        <strong>WH: включение / отключение магазинов</strong>
        <Badge bg="light" text="danger" className="ms-auto">
          PUT /wildberries/shops/&#123;shop_id&#125;
        </Badge>
      </Card.Header>
      <Card.Body className="p-3">
        <p className="text-muted small mb-3">
          Отключённые магазины сохраняются в базе — их можно снова включить. Поиск по email пользователя,
          названию или ID магазина.
        </p>

        <Row className="g-2 mb-3 align-items-end">
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small mb-0">Поиск</Form.Label>
              <Form.Control
                type="search"
                placeholder="Email, название, store_id, wh_shop_id…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4}>
            <Form.Group>
              <Form.Label className="small mb-0">Фильтр по пользователю</Form.Label>
              <AdminStoreSearchCombobox
                filterValue=""
                onFilterValueChange={() => {}}
                onStoreSelected={onUserPicked}
                valueField="id"
                placeholder="Email или магазин пользователя…"
                clearInputAfterPick
              />
              {userFilter && (
                <div className="mt-1 d-flex align-items-center gap-2">
                  <Badge bg="secondary">{userFilter.email}</Badge>
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={() => setUserFilter(null)}
                  >
                    Сбросить
                  </Button>
                </div>
              )}
            </Form.Group>
          </Col>
          <Col xs={12} md={4} className="d-flex flex-wrap gap-2 align-items-center">
            <Form.Check
              type="checkbox"
              id="wh-shops-disabled-only"
              label="Только отключённые нами"
              checked={disabledOnly}
              onChange={(e) => setDisabledOnly(e.target.checked)}
            />
            <Button variant="outline-danger" size="sm" onClick={loadStores} disabled={loading}>
              {loading ? <Spinner size="sm" animation="border" /> : 'Обновить'}
            </Button>
          </Col>
        </Row>

        {error && <div className="alert alert-danger py-2 small">{error}</div>}

        <div className="table-responsive">
          <Table size="sm" striped bordered hover className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Пользователь</th>
                <th>Магазин</th>
                <th>WB store_id</th>
                <th>WH shop_id</th>
                <th>Статус</th>
                <th style={{ minWidth: 200 }}>Действие</th>
              </tr>
            </thead>
            <tbody>
              {loading && stores.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-4">
                    <Spinner animation="border" size="sm" />
                  </td>
                </tr>
              ) : stores.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center text-muted py-3">
                    Нет магазинов с wh_shop_id по выбранным фильтрам
                  </td>
                </tr>
              ) : (
                stores.map((row) => {
                  const isInactive = row.admin_status === 'inactive';
                  const busy = togglingPk === row.store_pk;
                  return (
                    <tr key={row.store_pk}>
                      <td className="small">{row.user_email || `id:${row.user_id}`}</td>
                      <td>{row.store_name || '—'}</td>
                      <td className="font-monospace small">{row.store_external_id || '—'}</td>
                      <td className="font-monospace small text-break">{row.wh_shop_id}</td>
                      <td>
                        {row.admin_status === 'inactive' && (
                          <Badge bg="danger">inactive</Badge>
                        )}
                        {row.admin_status === 'active' && (
                          <Badge bg="success">active</Badge>
                        )}
                        {!row.admin_status && (
                          <Badge bg="secondary">—</Badge>
                        )}
                        {row.admin_status_at && (
                          <div className="text-muted" style={{ fontSize: '0.7rem' }}>
                            {row.admin_status_at.slice(0, 16).replace('T', ' ')}
                          </div>
                        )}
                      </td>
                      <td>
                        {isInactive ? (
                          <Button
                            variant="danger"
                            size="sm"
                            className="fw-semibold"
                            disabled={busy}
                            onClick={() => handleToggle(row, 'active')}
                          >
                            {busy ? <Spinner size="sm" animation="border" /> : 'Включить в WH'}
                          </Button>
                        ) : (
                          <Button
                            variant="danger"
                            size="sm"
                            className="fw-semibold"
                            disabled={busy}
                            onClick={() => handleToggle(row, 'inactive')}
                          >
                            {busy ? <Spinner size="sm" animation="border" /> : 'Отключить в WH'}
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mt-3">
          <div className="small text-muted">
            {total > 0 ? `Показано ${rangeFrom}–${rangeTo} из ${total}` : 'Нет записей'}
          </div>
          <div className="d-flex flex-wrap align-items-center gap-2">
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page <= 1 || loading}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Назад
            </Button>
            <span className="small text-muted">
              {page} / {totalPages}
            </span>
            <Button
              size="sm"
              variant="outline-secondary"
              disabled={page >= totalPages || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Вперёд
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
